/* =========================================================
   Sports n deco — Upload de l'aperçu print au checkout (voie B hybride).
   ---
   Pour les gammes que le worker Gelato ne peut PAS re-rendre (plaque = image
   texture externe, cyclisme = carte MapLibre/WebGL, col/kom avec tracé GPS
   importé), on rasterise l'aperçu EXACT affiché au client et on l'upload sur
   le worker (/upload → R2). L'URL part en propriété `_print_url` : le worker
   la wrappe en PDF → fidélité aperçu/print garantie.

   API (globale) :
     snPrintUpload.fromSvg(svgString, { fontCssUrls?, scale? }) -> Promise<{url}|null>
     snPrintUpload.fromElement(el, { scale? })                  -> Promise<{url}|null>
     snPrintUpload.FONTS = { sora, oswald }  (css2 Google Fonts à inliner)
   Échec silencieux (null) : l'ajout panier n'est JAMAIS bloqué.
   ========================================================= */
(function () {
  /* URL du worker Gelato (déployé 2026-07-08). Vide = upload désactivé
     (les adaptateurs continuent sans _print_url).
     Surchargeable via window.SPORTSNDECO_PRINT_UPLOAD_URL (défini AVANT ce script). */
  var UPLOAD_URL = window.SPORTSNDECO_PRINT_UPLOAD_URL
    || 'https://sportsndeco-shopify-to-gelato.davidsarabia-pro.workers.dev/upload';

  /* LARGEUR DE RASTÉRISATION PAR FORMAT — la page imprimée vaut le format
     + 4 mm de fond perdu par côté (cf. worker renderToPdf.js), d'où :
        dpi = largeur_px / ((format_mm + 8) / 25,4)
        30×40  → 2400 px = 198 dpi   (canvas  7,7 Mpx)
        50×70  → 3000 px = 150 dpi   (canvas 12,0 Mpx)  ← reco Gelato
        70×100 → 3400 px = 122 dpi   (canvas 15,4 Mpx)  ← plafond navigateur
     Avant (2400 px pour tous), le 70×100 tombait à 86 dpi.
     MAX_PIXELS : Safari iOS refuse/vide les canvas au-delà de ~16,7 Mpx —
     on reste dessous et on replie automatiquement si l'encodage échoue. */
  var TARGET_W_BY_SIZE = { '13x18': 1800, '30x40': 2400, '50x70': 3000, '70x100': 3400, digital: 3000 };
  var TARGET_W = 2400;         // défaut si le format n'est pas transmis
  var MAX_PIXELS = 16000000;
  var SAFE_BYTES = 22 * 1024 * 1024;  // marge sous la limite 24 Mo de /upload

  /* Largeur cible : opts.scale explicite > format du produit > défaut. */
  function targetWidth(opts) {
    if (opts && opts.scale) return Math.round(opts.scale);
    var s = opts && opts.size ? String(opts.size) : '';
    return TARGET_W_BY_SIZE[s] || TARGET_W;
  }

  function toBlob(canvas, type, q) {
    return new Promise(function (res) {
      try { canvas.toBlob(function (b) { res(b || null); }, type, q); }
      catch (e) { res(null); }
    });
  }

  /* PNG tant qu'il tient sous la limite (net sur les aplats et le texte),
     JPEG de repli au-delà (cartes et textures photo = PNG énorme). */
  async function encodeBest(canvas) {
    var png = await toBlob(canvas, 'image/png');
    if (png && png.size <= SAFE_BYTES) return png;
    var jpg = await toBlob(canvas, 'image/jpeg', 0.92);
    if (jpg && jpg.size <= SAFE_BYTES) {
      console.info('[print-upload] PNG ' + (png ? Math.round(png.size / 1048576) + ' Mo' : 'KO') +
        ' → bascule JPEG ' + Math.round(jpg.size / 1048576) + ' Mo');
      return jpg;
    }
    return null;   // le rappelant réessaiera plus petit
  }

  var FONTS = {
    sora:   'https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap',
    oswald: 'https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;500;600;700&display=swap',
  };

  function blobToDataUrl(blob) {
    return new Promise(function (res, rej) {
      var r = new FileReader();
      r.onload = function () { res(r.result); };
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
  }

  /* Inline les <image href="http..."> du SVG en data-URI (texture plaque…). */
  async function inlineImages(svgEl) {
    var imgs = svgEl.querySelectorAll('image');
    for (var i = 0; i < imgs.length; i++) {
      var el = imgs[i];
      var href = el.getAttribute('href') || el.getAttribute('xlink:href');
      if (!href || href.indexOf('data:') === 0) continue;
      try {
        var r = await fetch(href, { mode: 'cors' });
        if (!r.ok) throw new Error(r.status);
        var d = await blobToDataUrl(await r.blob());
        el.setAttribute('href', d);
        el.removeAttribute('xlink:href');
      } catch (e) { console.warn('[print-upload] image non inlinée:', href, e); }
    }
  }

  /* Récupère le CSS Google Fonts et inline chaque woff2 en data-URI.
     (Un SVG dans <img> est un document isolé : sans @font-face embarqué,
     le texte tomberait en police système.) Cache par URL. */
  var fontCssCache = {};
  async function inlineFontCss(cssUrl) {
    if (fontCssCache[cssUrl]) return fontCssCache[cssUrl];
    fontCssCache[cssUrl] = (async function () {
      var css = await (await fetch(cssUrl)).text();
      var urls = css.match(/url\((https:[^)]+)\)/g) || [];
      for (var i = 0; i < urls.length; i++) {
        var u = urls[i].slice(4, -1);
        try {
          var d = await blobToDataUrl(await (await fetch(u)).blob());
          css = css.split(u).join(d);
        } catch (e) { /* police manquante → fallback */ }
      }
      return css;
    })();
    return fontCssCache[cssUrl];
  }

  async function svgToPngBlob(svgString, fontCssUrls, wanted) {
    var doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
    var svg = doc.documentElement;
    if (svg.nodeName.toLowerCase() !== 'svg') throw new Error('SVG invalide');
    await inlineImages(svg);
    if (fontCssUrls && fontCssUrls.length) {
      var cssAll = '';
      for (var i = 0; i < fontCssUrls.length; i++) cssAll += await inlineFontCss(fontCssUrls[i]);
      var style = doc.createElementNS('http://www.w3.org/2000/svg', 'style');
      style.textContent = cssAll;
      svg.insertBefore(style, svg.firstChild);
    }
    var vb = (svg.getAttribute('viewBox') || '0 0 800 1066').split(/\s+/);
    var vw = parseFloat(vb[2]) || 800, vh = parseFloat(vb[3]) || 1066;
    var ratio = vh / vw;

    var ser = new XMLSerializer().serializeToString(svg);
    var svgUrl = URL.createObjectURL(new Blob([ser], { type: 'image/svg+xml;charset=utf-8' }));
    try {
      var img = await new Promise(function (res, rej) {
        var im = new Image();
        im.onload = function () { res(im); };
        im.onerror = function () { rej(new Error('SVG → Image KO')); };
        im.src = svgUrl;
      });
      /* Escalier de repli : si le canvas ou l'encodage échoue (mémoire iOS,
         blob trop lourd), on retente plus petit plutôt que de perdre la vente.
         L'impression reste toujours ≥ l'ancien niveau (2400 px). */
      var steps = [wanted, 2400, 1800].filter(function (v, i, a) { return v && a.indexOf(v) === i; });
      for (var s = 0; s < steps.length; s++) {
        var w = Math.round(steps[s]), h = Math.round(w * ratio);
        if (w * h > MAX_PIXELS) { h = Math.round(Math.sqrt(MAX_PIXELS * ratio)); w = Math.round(h / ratio); }
        try {
          var canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          var ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('contexte 2D indisponible');
          ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          var blob = await encodeBest(canvas);
          canvas.width = canvas.height = 1;      // libère la mémoire tout de suite
          if (blob) { console.info('[print-upload] rendu ' + w + '×' + h + ' · ' + blob.type + ' · ' + Math.round(blob.size / 1024) + ' Ko'); return blob; }
        } catch (e) { console.warn('[print-upload] échec à ' + w + ' px :', e && e.message); }
      }
      throw new Error('rastérisation impossible à toutes les tailles');
    } finally { URL.revokeObjectURL(svgUrl); }
  }

  var h2cPromise = null;
  function loadHtml2Canvas() {
    if (window.html2canvas) return Promise.resolve(window.html2canvas);
    if (!h2cPromise) {
      h2cPromise = new Promise(function (res, rej) {
        var s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
        s.onload = function () { res(window.html2canvas); };
        s.onerror = function () { h2cPromise = null; rej(new Error('html2canvas KO')); };
        document.head.appendChild(s);
      });
    }
    return h2cPromise;
  }

  async function upload(blob) {
    if (!UPLOAD_URL) return null;
    var type = (blob && blob.type) || 'image/png';
    var r = await fetch(UPLOAD_URL, { method: 'POST', headers: { 'Content-Type': type }, body: blob });
    if (!r.ok) throw new Error('upload ' + r.status);
    var j = await r.json();
    return j && j.url ? { url: j.url } : null;
  }

  /* Dernière erreur rencontrée — le driver la joint à la ligne panier
     (_print_error) pour qu'une commande sans fichier print soit traçable
     au lieu d'être découverte au moment de fabriquer. */
  var lastError = null;

  async function fromSvg(svgString, opts) {
    if (!UPLOAD_URL) return null;
    lastError = null;
    try {
      var blob = await svgToPngBlob(svgString, (opts && opts.fontCssUrls) || [], targetWidth(opts));
      return await upload(blob);
    } catch (e) {
      lastError = (e && e.message) || String(e);
      console.error('[print-upload] ÉCHEC fromSvg — la commande partira sans fichier print :', lastError);
      return null;
    }
  }

  async function fromElement(el, opts) {
    if (!UPLOAD_URL || !el) return null;
    lastError = null;
    try {
      var h2c = await loadHtml2Canvas();
      var target = targetWidth(opts);
      var scale = Math.max(1, Math.min(4, target / Math.max(1, el.clientWidth)));
      var canvas = await h2c(el, { scale: scale, useCORS: true, backgroundColor: null, logging: false });
      var blob = await encodeBest(canvas);
      if (!blob) throw new Error('encodage impossible (image trop lourde)');
      console.info('[print-upload] rendu élément ' + canvas.width + '×' + canvas.height + ' · ' + blob.type + ' · ' + Math.round(blob.size / 1024) + ' Ko');
      return await upload(blob);
    } catch (e) {
      lastError = (e && e.message) || String(e);
      console.error('[print-upload] ÉCHEC fromElement — la commande partira sans fichier print :', lastError);
      return null;
    }
  }

  /* Couleur de fond calculée d'un élément → hex (pour `_print_bg`). */
  function bgHexOf(el) {
    try {
      var c = getComputedStyle(el).backgroundColor || '';
      var m = c.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (!m) return '#ffffff';
      var hx = function (n) { return ('0' + Number(n).toString(16)).slice(-2); };
      return '#' + hx(m[1]) + hx(m[2]) + hx(m[3]);
    } catch (e) { return '#ffffff'; }
  }

  window.snPrintUpload = {
    fromSvg: fromSvg, fromElement: fromElement, bgHexOf: bgHexOf, FONTS: FONTS,
    lastError: function () { return lastError; },
    targetWidth: targetWidth,
    /* Diagnostic (banc de test tools/test-print-resolution.html) : rastérise
       sans uploader, pour mesurer taille, poids et temps par format. */
    rasterize: function (svgString, opts) {
      return svgToPngBlob(svgString, (opts && opts.fontCssUrls) || [], targetWidth(opts));
    },
  };
})();
